import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Clock, Route, Sun, Users } from "lucide-react";
import { api } from "../lib/api";
import { MaskedLine, Reveal, Stagger, StaggerItem } from "../components/Motion";
import Ribbon from "../components/Ribbon";

const HERO_IMG = "/api/files/salt-and-stone/hikes/e7d0145d-32c7-4088-8d07-b0bff39ca49e.webp";
const PORTRAIT = "/api/files/salt-and-stone/hikes/066708d2-bb84-4b55-b398-b28ce0fb7fb4.jpg";

function Hero({ img }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex h-[100svh] items-end overflow-hidden bg-deep">
      {img && (
        <motion.div
          style={{ y }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="absolute inset-0 scale-110"
        >
          <img src={img} alt="Rocky cliff path above the Aegean sea on Hydra island" className="h-full w-full object-cover brightness-110 saturate-110" />
        </motion.div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-deep/70 via-deep/10 to-deep/20" />

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 sm:px-8 sm:pb-24">
        <MaskedLine delay={0.2}>
          <span className="overline-label text-paper/70">Guided hikes · Hydra, Greece</span>
        </MaskedLine>
        <MaskedLine delay={0.3}>
          <span className="mt-7 block font-serif text-4xl italic tracking-tight text-terra-light [text-shadow:0_2px_18px_rgba(15,23,42,0.65)]" data-testid="hero-brand-mark">Salt &amp; Stone</span>
        </MaskedLine>
        <h1 className="display-serif mt-3 text-paper" data-testid="hero-title">
          <MaskedLine delay={0.35} className="text-[13vw] leading-[0.92] sm:text-7xl lg:text-8xl">
            Where the sea
          </MaskedLine>
          <MaskedLine delay={0.5} className="text-[13vw] leading-[0.92] sm:text-7xl lg:text-8xl">
            meets <em className="not-italic text-terra-light [text-shadow:0_2px_18px_rgba(15,23,42,0.65)]">the summit.</em>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.75}>
          <p className="mt-6 max-w-md text-lg leading-relaxed text-paper/80 sm:text-lg">
            Private hikes across Hydra's mule paths, monasteries and hidden coves —
            led by Vasilis Kastritseas, the island's own guide.
          </p>
        </MaskedLine>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/hikes"
            data-testid="hero-explore-btn"
            className="group flex items-center gap-2 rounded-full bg-terra px-8 py-4 text-base uppercase tracking-[0.15em] text-paper transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-terra-dark"
          >
            Explore the hikes
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/about"
            data-testid="hero-about-link"
            className="text-base uppercase tracking-[0.2em] text-paper/80 underline decoration-paper/30 underline-offset-8 transition-colors duration-300 hover:text-paper hover:decoration-paper"
          >
            Meet Vasilis
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 right-6 z-10 hidden flex-col items-center gap-2 text-paper/60 sm:flex"
      >
        <span className="text-sm uppercase tracking-[0.3em] [writing-mode:vertical-lr]">Scroll</span>
        <span className="h-10 w-px animate-slow-pulse bg-paper/60" />
      </motion.div>
    </section>
  );
}

function Intro() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} data-testid="intro-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <div className="grid gap-14 md:grid-cols-12 md:gap-8">
        <div className="md:col-span-5">
          <Reveal>
            <div className="relative">
              <div className="aspect-[3/4] overflow-hidden rounded-2xl">
                <motion.img
                  style={{ y }}
                  src={PORTRAIT}
                  alt="Vasilis Kastritseas, hiking guide of Hydra"
                  className="h-[110%] w-full object-cover"
                />
              </div>
              <p className="mt-3 text-sm uppercase tracking-[0.25em] text-ink/50">
                Vasilis Kastritseas — guide, islander, first aider
              </p>
            </div>
          </Reveal>
        </div>
        <div className="flex flex-col justify-center md:col-span-6 md:col-start-7">
          <Reveal>
            <span className="overline-label text-terra">Your guide</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="display-serif mt-5 text-4xl leading-[1.02] sm:text-5xl lg:text-6xl" data-testid="intro-title">
              The island is the itinerary. Vasilis is the key.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-7 text-lg leading-relaxed text-ink/70 sm:text-lg">
              Hydra has no cars — only stone mule paths climbing from the harbour into pine
              forest, monastery silence and cliffs above the Saronic Gulf. Vasilis grew up
              walking them. As a qualified physical education professional and accredited
              first aider, he reads the mountain so you can simply walk it.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-9 flex flex-wrap gap-2" data-testid="intro-credentials">
              {["Physical Education Professional", "Accredited First Aider", "Advanced Hiker", "Fluent English & Greek"].map((c) => (
                <span key={c} className="rounded-full border border-ink/15 px-4 py-1.5 text-sm uppercase tracking-[0.12em] text-ink/60">
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <Link
              to="/about"
              data-testid="intro-story-link"
              className="group mt-10 inline-flex items-center gap-2 text-base uppercase tracking-[0.2em] text-terra"
            >
              Read his story
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FeaturedHikes() {
  const [hikes, setHikes] = useState([]);
  useEffect(() => {
    api.get("/hikes").then((r) => setHikes(r.data.filter((h) => h.featured))).catch(() => {});
  }, []);

  if (!hikes.length) return null;

  return (
    <section data-testid="featured-hikes" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <div className="flex items-end justify-between gap-6">
        <Reveal>
          <div>
            <span className="overline-label text-terra">The collection</span>
            <h2 className="display-serif mt-4 text-4xl leading-none sm:text-5xl lg:text-6xl">Featured hikes</h2>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <Link
            to="/hikes"
            data-testid="featured-view-all"
            className="group hidden items-center gap-2 text-base uppercase tracking-[0.2em] text-ink/60 transition-colors duration-300 hover:text-terra sm:flex"
          >
            All five trails
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>

      <Stagger className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {hikes.map((hike, i) => (
          <StaggerItem key={hike.slug}>
            <Link to={`/hikes/${hike.slug}`} data-testid={`featured-hike-${hike.slug}`} className="group block">
              <div className="relative aspect-[16/10] overflow-hidden rounded-2xl">
                <img
                  src={hike.image}
                  alt={hike.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-paper/90 text-sm font-semibold tracking-wider text-ink backdrop-blur" data-testid={`featured-num-${hike.slug}`}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="absolute left-16 top-4 rounded-full bg-terra/90 px-4 py-1.5 text-sm uppercase tracking-[0.15em] text-paper backdrop-blur" data-testid={`featured-difficulty-${hike.slug}`}>
                  {hike.difficulty}
                </span>
              </div>
              <div className="mt-4 flex items-baseline justify-between gap-3">
                <h3 className="font-serif text-2xl tracking-tight">{hike.name}</h3>
                <span className="shrink-0 text-lg font-medium text-terra">{hike.price}</span>
              </div>
              <p className="mt-1.5 text-base leading-relaxed text-ink/60">{hike.tagline}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-sm uppercase tracking-[0.08em] text-ink/55" data-testid={`featured-meta-${hike.slug}`}>
                <span className="flex items-center gap-1.5 rounded-full bg-olive-pale px-3 py-1"><Route size={13} className="text-terra" /> {hike.distance}</span>
                <span className="flex items-center gap-1.5 rounded-full bg-olive-pale px-3 py-1"><Clock size={13} className="text-terra" /> {hike.duration}</span>
                <span className="flex items-center gap-1.5 rounded-full bg-olive-pale px-3 py-1"><Users size={13} className="text-terra" /> {hike.group_size}</span>
                <span className="flex items-center gap-1.5 rounded-full bg-olive-pale px-3 py-1"><Sun size={13} className="text-terra" /> {hike.season || "All year"}</span>
              </div>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function Manifesto() {
  const chapters = [
    { n: "01", title: "No engines, only footsteps", body: "Hydra banned the car. What remains is the original speed of travel — and an island that still sounds like wind, bells and the sea." },
    { n: "02", title: "Small groups, real places", body: "Never more than six walkers. Monasteries, farmsteads and coves that don't appear on the ferry brochure." },
    { n: "03", title: "Walk with the island", body: "Vasilis doesn't follow trails — he grew up on them. Every route bends to the light, the season and your legs." },
  ];
  return (
    <section data-testid="manifesto-section" className="bg-deep py-24 text-paper sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <span className="overline-label text-terra-light">The manifesto</span>
        </Reveal>
        <div className="mt-12">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div className="grid gap-4 border-t border-paper/10 py-10 sm:grid-cols-12 sm:gap-8" data-testid={`manifesto-chapter-${c.n}`}>
                <span className="font-serif text-6xl font-light text-terra-light/40 sm:col-span-2 sm:text-7xl">{c.n}</span>
                <h3 className="font-serif text-3xl tracking-tight sm:col-span-4 sm:text-4xl">{c.title}</h3>
                <p className="max-w-md text-lg leading-relaxed text-paper/60 sm:col-span-5 sm:col-start-8">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section data-testid="final-cta" className="mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
      <Reveal>
        <p className="overline-label text-terra">Ready when you are</p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="display-serif mt-6 max-w-4xl text-5xl leading-[0.95] sm:text-6xl lg:text-7xl">
          The mountain is <em className="not-italic text-terra">already awake.</em>
        </h2>
      </Reveal>
      <Reveal delay={0.2}>
        <Link
          to="/hikes"
          data-testid="cta-book-btn"
          className="group mt-10 inline-flex items-center gap-3 rounded-full bg-ink px-9 py-4 text-base uppercase tracking-[0.15em] text-paper transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-terra"
        >
          Choose your hike
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </section>
  );
}

export default function Home() {
  const [heroImg, setHeroImg] = useState(null);
  useEffect(() => {
    document.title = "Salt & Stone — Guided Hikes on Hydra Island, Greece";
    api.get("/settings")
      .then((r) => setHeroImg(r.data.hero_image || HERO_IMG))
      .catch(() => setHeroImg(HERO_IMG));
  }, []);
  return (
    <main data-testid="home-page">
      <Hero img={heroImg} />
      <Ribbon />
      <Intro />
      <FeaturedHikes />
      <Manifesto />
      <Ribbon dark />
      <FinalCta />
    </main>
  );
}
